import { Fragment, useEffect, useState } from "react";
import { api, fmtDate, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Trash2,
  Loader2,
  Search,
  Server,
  History,
  Receipt,
  RefreshCw,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import { toast } from "sonner";

const invoiceHistoryTimestamp = (invoice) => {
  const date = invoice.status === "paid" && invoice.paid_at
    ? invoice.paid_at
    : invoice.created_at;
  const timestamp = Date.parse(date || "");
  return Number.isFinite(timestamp) ? timestamp : 0;
};

export default function AdminClients() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const [search, setSearch] = useState("");
  const [expandedId, setExpandedId] = useState(null);
  const [clientVps, setClientVps] = useState({});
  const [loadingVpsId, setLoadingVpsId] = useState(null);
  const [expandedHistoryId, setExpandedHistoryId] = useState(null);
  const [clientInvoices, setClientInvoices] = useState({});
  const [loadingInvoicesId, setLoadingInvoicesId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/clients");
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const toggleClientVps = async (client) => {
    if (expandedId === client.user_id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(client.user_id);
    if (clientVps[client.user_id]) return;
    setLoadingVpsId(client.user_id);
    try {
      const { data } = await api.get(`/admin/clients/${client.user_id}/vps`);
      setClientVps((current) => ({ ...current, [client.user_id]: data }));
    } catch (e) {
      toast.error(formatApiError(e));
      setExpandedId(null);
    } finally {
      setLoadingVpsId(null);
    }
  };

  const loadClientHistory = async (client, closeOnError = false) => {
    setLoadingInvoicesId(client.user_id);
    try {
      const { data } = await api.get(
        `/admin/clients/${client.user_id}/invoices`,
      );
      setClientInvoices((current) => ({ ...current, [client.user_id]: data }));
    } catch (e) {
      toast.error(formatApiError(e));
      if (closeOnError) setExpandedHistoryId(null);
    } finally {
      setLoadingInvoicesId(null);
    }
  };

  const toggleClientHistory = async (client) => {
    if (expandedHistoryId === client.user_id) {
      setExpandedHistoryId(null);
      return;
    }
    setExpandedHistoryId(client.user_id);
    await loadClientHistory(client, true);
  };

  const remove = async (c) => {
    const ok = window.confirm(
      `Apagar cliente "${c.name}" (${c.email})?\n\nISTO IRÁ APAGAR TAMBÉM:\n• Todas as VPS deste cliente (também serão terminadas no portal FLIX)\n• Todas as faturas\n• Todos os tickets\n• Todas as sessões\n\nEsta ação NÃO pode ser desfeita.`
    );
    if (!ok) return;
    setDeletingId(c.user_id);
    try {
      const { data } = await api.delete(`/admin/clients/${c.user_id}`);
      const d = data.deleted || {};
      toast.success(
        `Cliente apagado. Removidos: ${d.vps || 0} VPS, ${d.invoices || 0} faturas, ${d.tickets || 0} tickets.`
      );
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDeletingId(null);
    }
  };

  const shown = items
    .filter((client) => {
      const term = search.trim().toLowerCase();
      return !term || `${client.name || ""} ${client.email || ""}`.toLowerCase().includes(term);
    })
    .sort((a, b) =>
      (a.name || a.email || "").localeCompare(b.name || b.email || "", "pt-BR", { sensitivity: "base" })
    );

  return (
    <div className="space-y-6" data-testid="admin-clients-page">
      <div>
        <div className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500 mb-2">// admin · clientes</div>
        <h1 className="font-heading text-3xl font-bold tracking-tighter">Clientes</h1>
      </div>
      <label className="flex max-w-md items-center gap-2 rounded-sm border border-white/10 bg-[#09090B] px-3 py-2">
        <Search className="h-4 w-4 shrink-0 text-zinc-500" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar por nome ou email..."
          className="w-full bg-transparent font-mono text-xs text-white outline-none placeholder:text-zinc-600"
          data-testid="admin-clients-search"
        />
      </label>
      <div className="border border-white/10 bg-[#121214]">
        <table className="w-full text-sm">
          <thead className="text-xs font-mono uppercase tracking-widest text-zinc-500">
            <tr className="border-b border-white/10">
              <th className="text-left px-5 py-3">Nome</th>
              <th className="text-left px-5 py-3">Email</th>
              <th className="text-left px-5 py-3">Provider</th>
              <th className="text-left px-5 py-3">Criado</th>
              <th className="text-right px-5 py-3">Ações</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((c) => {
              const vps = clientVps[c.user_id];
              const expanded = expandedId === c.user_id;
              const invoices = clientInvoices[c.user_id];
              const historyInvoices = invoices
                ? [...invoices].sort(
                    (a, b) => invoiceHistoryTimestamp(b) - invoiceHistoryTimestamp(a),
                  )
                : [];
              const historyExpanded = expandedHistoryId === c.user_id;
              return (
                <Fragment key={c.user_id}>
                  <tr className="border-b border-white/5 hover:bg-white/5" data-testid={`admin-client-row-${c.user_id}`}>
                    <td className="px-5 py-3">
                      <button
                        type="button"
                        onClick={() => toggleClientVps(c)}
                        disabled={loadingVpsId === c.user_id}
                        className="text-left text-emerald-300 hover:text-white disabled:opacity-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-emerald-400"
                        aria-expanded={expanded}
                        aria-label={`Ver VPS de ${c.name || c.email}`}
                      >
                        {c.name || c.email || <span className="text-zinc-500">—</span>}
                      </button>
                    </td>
                    <td className="px-5 py-3 font-mono">{c.email}</td>
                    <td className="px-5 py-3 font-mono text-xs">{c.auth_provider}</td>
                    <td className="px-5 py-3 font-mono text-xs">{fmtDate(c.created_at)}</td>
                    <td className="px-5 py-3 text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => toggleClientVps(c)}
                          disabled={loadingVpsId === c.user_id}
                          className="h-8 rounded-sm border-white/15 px-3 text-zinc-300 hover:bg-white/10 hover:text-white"
                          data-testid={`admin-client-vps-${c.user_id}`}
                          aria-expanded={expanded}
                        >
                          {loadingVpsId === c.user_id
                            ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                            : expanded ? <ChevronDown className="mr-1 h-3.5 w-3.5" /> : <ChevronRight className="mr-1 h-3.5 w-3.5" />}
                          {vps ? `VPS (${vps.length})` : "Ver VPS"}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => toggleClientHistory(c)}
                          disabled={loadingInvoicesId !== null}
                          className="h-8 rounded-sm border-white/15 px-3 text-zinc-300 hover:bg-white/10 hover:text-white"
                          data-testid={`admin-client-history-${c.user_id}`}
                          aria-expanded={historyExpanded}
                          aria-label={`Ver histórico de faturas de ${c.name || c.email}`}
                        >
                          {loadingInvoicesId === c.user_id ? (
                            <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <History className="mr-1 h-3.5 w-3.5" />
                          )}
                          Histórico
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => remove(c)}
                          disabled={deletingId === c.user_id}
                          className="h-8 rounded-sm border-red-500/40 px-3 text-red-400 hover:border-red-500 hover:bg-red-500 hover:text-white"
                          data-testid={`admin-client-delete-${c.user_id}`}
                        >
                          {deletingId === c.user_id
                            ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            : <Trash2 className="h-3.5 w-3.5" />}
                        </Button>
                      </div>
                    </td>
                  </tr>
                  {expanded && (
                    <tr className="border-b border-white/10 bg-black/20" data-testid={`admin-client-vps-row-${c.user_id}`}>
                      <td colSpan={5} className="px-5 py-4">
                        <div className="mb-3 flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-zinc-400">
                          <Server className="h-3.5 w-3.5" /> VPS ativas e vinculadas a {c.name || c.email}
                        </div>
                        {!vps?.length ? (
                          <div className="text-sm text-zinc-500">Este cliente não possui VPS ativa vinculada.</div>
                        ) : (
                          <div className="space-y-2">
                            {vps.map((item) => (
                              <div key={item.vps_id} className="grid gap-2 border border-white/10 px-3 py-2 text-xs md:grid-cols-[1.4fr_1fr_1fr_auto] md:items-center">
                                <div>
                                  <div className="font-mono text-white">{item.hostname || item.vps_id}</div>
                                  <div className="text-zinc-500">{item.provider === "brasil" ? "Brasil" : "França"} · {item.os}</div>
                                </div>
                                <div className="font-mono text-zinc-300">{item.ipv4 || "IP pendente"}</div>
                                <div className="font-mono text-zinc-500">{item.cpu_cores} vCPU · {item.ram_gb} GB RAM · {item.ssd_gb} GB SSD</div>
                                <div className="font-mono uppercase text-emerald-300">{item.status}</div>
                              </div>
                            ))}
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                  {historyExpanded && (
                    <tr
                      className="border-b border-white/10 bg-black/20"
                      data-testid={`admin-client-history-row-${c.user_id}`}
                    >
                      <td colSpan={5} className="px-5 py-4">
                        <div className="mb-3 flex items-center justify-between gap-3">
                          <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-zinc-400">
                            <Receipt className="h-3.5 w-3.5" /> Faturas e renovações de {c.name || c.email}
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => loadClientHistory(c)}
                            disabled={loadingInvoicesId !== null}
                            className="h-8 shrink-0 rounded-sm border-white/15 px-3 text-zinc-300 hover:bg-white/10 hover:text-white"
                            data-testid={`admin-client-history-refresh-${c.user_id}`}
                          >
                            {loadingInvoicesId === c.user_id ? (
                              <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <RefreshCw className="mr-1 h-3.5 w-3.5" />
                            )}
                            Atualizar
                          </Button>
                        </div>
                        {loadingInvoicesId === c.user_id ? (
                          <div className="text-sm font-mono text-zinc-500">Carregando histórico…</div>
                        ) : !historyInvoices.length ? (
                          <div className="text-sm text-zinc-500">Este cliente ainda não possui faturas.</div>
                        ) : (
                          <div className="space-y-2">
                            {historyInvoices.map((invoice) => {
                              const type = invoice.is_renewal
                                ? "Renovação"
                                : invoice.is_plan_upgrade
                                  ? "Upgrade"
                                  : "Compra";
                              const statusLabels = {
                                paid: "Paga",
                                pending: "Pendente",
                                cancelled: "Cancelada",
                                expired: "Expirada",
                                failed: "Falhou",
                                refunded: "Reembolsada",
                              };
                              return (
                                <div
                                  key={invoice.invoice_id}
                                  className="grid gap-2 border border-white/10 px-3 py-2 text-xs md:grid-cols-[1.3fr_1fr_1fr_1fr_1fr]"
                                  data-testid={`admin-client-invoice-${invoice.invoice_id}`}
                                >
                                  <div>
                                    <div className="font-mono text-white">
                                      {invoice.number || invoice.invoice_id}
                                    </div>
                                    <div className="text-zinc-500">
                                      {type}
                                      {invoice.plan_name
                                        ? ` · ${invoice.plan_name}`
                                        : ""}
                                    </div>
                                  </div>
                                  <div>
                                    <div className="text-zinc-500">
                                      {invoice.status === "paid" && invoice.paid_at
                                        ? "Pago em"
                                        : "Emitida em"}
                                    </div>
                                    <div className="font-mono text-zinc-300">
                                      {fmtDate(
                                        invoice.status === "paid" && invoice.paid_at
                                          ? invoice.paid_at
                                          : invoice.created_at,
                                      )}
                                    </div>
                                  </div>
                                  <div>
                                    <div className="text-zinc-500">Valor</div>
                                    <div className="font-mono text-zinc-300">
                                      {new Intl.NumberFormat("pt-BR", {
                                        style: "currency",
                                        currency: "BRL",
                                      }).format(Number(invoice.amount_brl || 0))}
                                    </div>
                                  </div>
                                  <div>
                                    <div className="text-zinc-500">Status</div>
                                    <div className="font-mono text-zinc-300">
                                      {statusLabels[invoice.status] ||
                                        invoice.status ||
                                        "—"}
                                    </div>
                                  </div>
                                  <div>
                                    <div className="text-zinc-500">Pagamento</div>
                                    <div className="font-mono text-zinc-300">{invoice.payment_method || "—"}</div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
            {shown.length === 0 && !loading && (
              <tr><td colSpan={5} className="p-10 text-center text-zinc-500">Sem clientes.</td></tr>
            )}
            {loading && (
              <tr><td colSpan={5} className="p-10 text-center text-zinc-500 font-mono text-xs">Carregando…</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
